import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const STAGES = [
  {
    id: "tofu",
    label: "Top of Funnel",
    sub: "Awareness & Education",
    mechanism: "Content Syndication, Digital Whitepaper Downloads, Category Research Engagement, Opt-in Marketing Qualified Leads (MQLs).",
    outcome: "Expands brand visibility across your total addressable market (TAM) with compliant opt-in contact capture.",
    stats: [
      ["PRIMARY OUTPUT", "Opt-in MQLs"],
      ["LEAD DEPTH", "Contact + Consent"],
      ["TYPICAL CPL TIER", "Volume"],
    ],
  },
  {
    id: "mofu",
    label: "Middle of Funnel",
    sub: "Consideration & Evaluation",
    mechanism: "Multi-Touch Lead Nurturing, Solution Comparison Guides, High-Quality Leads (HQL), Webinar Registrations, Install-Base Competitive Displacements.",
    outcome: "Filters out passive readers and identifies accounts actively benchmarking technology vendors and architectures.",
    stats: [
      ["PRIMARY OUTPUT", "HQLs · Registrants"],
      ["LEAD DEPTH", "+ Discovery Answers"],
      ["TYPICAL CPL TIER", "Performance"],
    ],
  },
  {
    id: "bofu",
    label: "Bottom of Funnel",
    sub: "Purchase Intent & Decision",
    mechanism: "BANT-Qualified Opportunities, Sales Qualified Leads (SQL), Confirmed Call-Backs (CCL), Direct Sales Appointments.",
    outcome: "Delivers enterprise decision-makers with confirmed budget, direct purchase authority, acute project pain points, and active deployment timelines.",
    stats: [
      ["PRIMARY OUTPUT", "BANT · CCL"],
      ["LEAD DEPTH", "Budget + Timeline"],
      ["TYPICAL CPL TIER", "Premium"],
    ],
  },
];

export const FunnelSelector = () => {
  const [active, setActive] = useState(0);
  const stage = STAGES[active];

  return (
    <div data-testid="funnel-selector">
      <div className="flex flex-col gap-2 sm:flex-row">
        {STAGES.map((s, i) => (
          <button
            key={s.id}
            data-testid={`funnel-tab-${s.id}`}
            onClick={() => setActive(i)}
            className={`relative flex-1 rounded-xl border px-5 py-4 text-left transition-all duration-300 ${
              active === i
                ? "border-cobalt bg-navy text-white shadow-[0_16px_40px_-16px_rgba(11,25,44,0.5)]"
                : "border-line bg-white text-charcoal hover:border-cobalt/50"
            }`}
          >
            <span className={`font-mono2 text-[10px] tracking-[0.22em] ${active === i ? "text-flame" : "text-cobalt"}`}>
              0{i + 1}
            </span>
            <span className="mt-1 block font-jakarta text-base font-bold">{s.label}</span>
            <span className={`block text-xs ${active === i ? "text-slate-300" : "text-slate-500"}`}>{s.sub}</span>
            {active === i && (
              <motion.span layoutId="funnel-active" className="absolute inset-x-5 bottom-0 h-0.5 bg-flame" />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={stage.id}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 grid gap-8 rounded-2xl border border-line bg-white p-7 sm:p-10 lg:grid-cols-5"
          data-testid={`funnel-panel-${stage.id}`}
        >
          <div className="lg:col-span-3">
            <p className="font-mono2 text-[10px] tracking-[0.24em] text-cobalt">VYACOMM EXECUTION MECHANISM</p>
            <p className="mt-3 text-base leading-relaxed text-charcoal sm:text-lg">{stage.mechanism}</p>
            <div className="mt-6 flex items-start gap-3 rounded-xl bg-canvas p-5">
              <ArrowDown className="mt-1 h-4 w-4 shrink-0 text-flame" />
              <p className="text-sm leading-relaxed text-slate-600">
                <span className="font-semibold text-navy">Commercial Pipeline Outcome — </span>
                {stage.outcome}
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-between gap-6 lg:col-span-2">
            <div className="space-y-4">
              {stage.stats.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-line pb-3">
                  <span className="font-mono2 text-[10px] tracking-[0.18em] text-slate-400">{k}</span>
                  <span className="text-sm font-bold text-navy">{v}</span>
                </div>
              ))}
            </div>
            <Link
              to="/contact-us"
              data-testid={`funnel-cta-${stage.id}`}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-cobalt px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#1d4fd7]"
            >
              Build a {stage.label} Program
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
